/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { ApiDataModel } from "../ApiDataModel";
import { ApiRouteFormatter, getApiKey, getApiVersion, isCloudRunEnabled } from "../ApiWeb";
import { Network } from "./network";

type IApiModel = ApiDataModel.IModel;

let _rootDiv: HTMLDivElement | undefined = undefined;
let _currentModelsHtmlListElement: HTMLUListElement | undefined = undefined;
let _currentSelectedModelHtmlDivElement: HTMLDivElement | undefined = undefined;

let _accessToken: string = "";
let _onModelSelected: Function | undefined = undefined;
let _currentModels: IApiModel[] = [];

export namespace UIModel {
    export function Bind(accessToken: string, workspaceId: string, onModelSelected: Function): void {
        _accessToken = accessToken;
        _onModelSelected = onModelSelected;

        _rootDiv = _InitializeUI(accessToken, workspaceId);
        document.body.appendChild(_rootDiv);
    }
    export function Unbind() {
        if (_rootDiv) {
            _rootDiv.remove();
        }

        _currentModels = [];

        if (_currentModelsHtmlListElement) {
            _currentModelsHtmlListElement = undefined;
        }

        if (_currentSelectedModelHtmlDivElement) {
            _currentSelectedModelHtmlDivElement.style.fontWeight = "";
            _currentSelectedModelHtmlDivElement.style.textDecoration = "";
        }
    }

    function _InitializeUI(accessToken: string, workspaceId: string): HTMLDivElement {
        let rootDiv = document.createElement("div");
        rootDiv.id = "ui-models-root-div";

        // title
        let titleElement = document.createElement("h2");
        titleElement.innerText = "Models";
        rootDiv.appendChild(titleElement);

        // get button
        let getButton = document.createElement("button");
        getButton.id = "ui-models-get-button";
        getButton.textContent = "Get Models";
        rootDiv.appendChild(getButton);
        rootDiv.appendChild(document.createElement("br"));
        getButton.addEventListener("click", async () => {
            _currentModels = await _FetchModels(accessToken, workspaceId);
            _currentModelsHtmlListElement = _RefreshUIModels(_currentModelsHtmlListElement, _currentModels);
        });

        // name input
        let nameInput = document.createElement("input");
        nameInput.id = "ui-models-create-input";
        let nameLabelElement = document.createElement("label");
        nameLabelElement.textContent = "Model name";
        nameLabelElement.setAttribute("for", nameInput.id);
        rootDiv.appendChild(nameLabelElement);
        rootDiv.appendChild(nameInput);

        // create button
        let createButton = document.createElement("button");
        createButton.id = "ui-models-create-button";
        createButton.textContent = "Create Model";
        rootDiv.appendChild(createButton);
        createButton.addEventListener("click", async () => {
            if (nameInput) {
                // @ts-ignore
                const sanitizedValue: string = DOMPurify.sanitize(nameInput.value, { USE_PROFILES: { html: false } });
                await _CreateModel(accessToken, workspaceId, sanitizedValue);
                _currentModels = await _FetchModels(accessToken, workspaceId);
                _currentModelsHtmlListElement = _RefreshUIModels(_currentModelsHtmlListElement, _currentModels);
            }
        });

        // update button
        const updateButton: HTMLButtonElement = document.createElement("button");
        updateButton.id = "ui-model-update-button";
        updateButton.textContent = "Update Model";
        rootDiv.appendChild(updateButton);
        updateButton.addEventListener("click", async () => {
            if (!_currentSelectedModelHtmlDivElement) {
                throw new Error("Invalid/Missing current selected model");
            }

            // @ts-ignore
            const sanitizedValue: string = DOMPurify.sanitize(nameInput.value, { USE_PROFILES: { html: false } });
            const apiModel: IApiModel = await _UpdateModel(accessToken, workspaceId, _currentSelectedModelHtmlDivElement.id, sanitizedValue);
            _UpdateUIModelsFromModel(apiModel);
        });

        // list of models
        _currentModelsHtmlListElement = _CreateUIModels(_currentModels);
        rootDiv.appendChild(_currentModelsHtmlListElement);

        return rootDiv;
    }
    function _CreateUIModels(apiModels: IApiModel[] = []): HTMLUListElement {
        const oList = document.createElement("ul");

        const sortedApiModels: IApiModel[] = apiModels.sort(
            (apiModelA, apiModelB) => new Date(apiModelA.created_at).getTime() - new Date(apiModelB.created_at).getTime()
        );

        for (let cApiModel of sortedApiModels) {
            let cListElement: HTMLLIElement = document.createElement("li");
            cListElement.style.display = "flex";
            cListElement.style.alignItems = "center";

            let cModelDiv: HTMLDivElement = document.createElement("div");
            cModelDiv.id = cApiModel.id;
            cModelDiv.textContent = `model ${cApiModel.id} | ${cApiModel.name} | created at: ${cApiModel.created_at} | updated at: ${cApiModel.updated_at}`;
            cListElement.appendChild(cModelDiv);
            cModelDiv.addEventListener("click", () => {
                // Remove bold + underline
                if (_currentSelectedModelHtmlDivElement) {
                    _currentSelectedModelHtmlDivElement.style.fontWeight = "";
                    _currentSelectedModelHtmlDivElement.style.textDecoration = "";
                }

                // Add bold + underline
                cModelDiv.style.fontWeight = "bold";
                cModelDiv.style.textDecoration = "underline";

                // Swap selected model
                _currentSelectedModelHtmlDivElement = cModelDiv;

                // Call next
                if (_onModelSelected) {
                    _onModelSelected(_accessToken, cApiModel.workspaceId, cApiModel.id);
                }
            });

            // get button
            let cGetButton: HTMLButtonElement = document.createElement("button");
            cGetButton.id = "ui-model-get-button-" + cApiModel.id;
            cGetButton.textContent = "Get";
            cGetButton.style.marginLeft = "10px";
            cListElement.appendChild(cGetButton);
            cGetButton.addEventListener("click", async () => {
                const apiModel: IApiModel = await _FetchModel(_accessToken, cApiModel.workspaceId, cApiModel.id);
                _UpdateUIModelsFromModel(apiModel);
            });

            // delete button
            let cDeleteButton: HTMLButtonElement = document.createElement("button");
            cDeleteButton.id = "ui-model-delete-button-" + cApiModel.id;
            cDeleteButton.textContent = "Delete";
            cDeleteButton.style.marginLeft = "10px";
            cListElement.appendChild(cDeleteButton);
            cDeleteButton.addEventListener("click", async () => {
                await _DeleteModel(_accessToken, cApiModel.workspaceId, cApiModel.id);
                _currentModels = await _FetchModels(_accessToken, cApiModel.workspaceId);
                _currentModelsHtmlListElement = _RefreshUIModels(_currentModelsHtmlListElement, _currentModels);
            });

            oList.appendChild(cListElement);
        }

        return oList;
    }
    function _RefreshUIModels(currentList?: HTMLUListElement, apiModels: IApiModel[] = []): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUIModels(apiModels);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    function _UpdateUIModelsFromModel(apiModel: IApiModel): void {
        const updatedModels: IApiModel[] = _currentModels.map(cModel => (apiModel.id === cModel.id ? apiModel : cModel));
        _currentModelsHtmlListElement = _RefreshUIModels(_currentModelsHtmlListElement, updatedModels);

        if (_currentSelectedModelHtmlDivElement) {
            _currentSelectedModelHtmlDivElement.style.fontWeight = "";
            _currentSelectedModelHtmlDivElement.style.textDecoration = "";
            _currentSelectedModelHtmlDivElement = undefined;
        }
    }

    async function _CreateModel(accessToken: string, workspaceId: string, name: string): Promise<IApiModel> {
        const apiModelsUrl: string = ApiRouteFormatter.FormatMeModelsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId);
        const createMeModelResponse: { model: IApiModel } = await Network.PostMeModels(apiModelsUrl, getApiKey(), accessToken, name);
        return createMeModelResponse.model;
    }
    async function _FetchModels(accessToken: string, workspaceId: string): Promise<IApiModel[]> {
        const apiModelsUrl: string = ApiRouteFormatter.FormatMeModelsUrl(isCloudRunEnabled(), getApiVersion(), workspaceId);
        const getMeModelsResponse: { models: IApiModel[] } = await Network.GetMeModels(apiModelsUrl, getApiKey(), accessToken);
        return getMeModelsResponse.models || [];
    }
    async function _FetchModel(accessToken: string, workspaceId: string, modelId: string): Promise<IApiModel> {
        const apiModelsUrl: string = ApiRouteFormatter.FormatMeModelUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, modelId);
        const getMeModelResponse: { model: IApiModel } = await Network.GetMeModel(apiModelsUrl, getApiKey(), accessToken);
        return getMeModelResponse.model;
    }
    async function _UpdateModel(accessToken: string, workspaceId: string, modelId: string, name: string): Promise<IApiModel> {
        const apiModelsUrl: string = ApiRouteFormatter.FormatMeModelUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, modelId);
        const updateMeModelResponse: { model: IApiModel } = await Network.PutMeModel(apiModelsUrl, getApiKey(), accessToken, name);
        return updateMeModelResponse.model;
    }
    async function _DeleteModel(accessToken: string, workspaceId: string, modelId: string): Promise<void> {
        const apiModelsUrl: string = ApiRouteFormatter.FormatMeModelUrl(isCloudRunEnabled(), getApiVersion(), workspaceId, modelId);
        await Network.DeleteMeModel(apiModelsUrl, getApiKey(), accessToken);
    }
}
